/**
 * components/admin/StorefrontQRCard.jsx · v2.7.58+E40
 * ═══════════════════════════════════════════════════════════════════════
 * Admin uploads the storefront-wide Cash App + Venmo QR codes displayed
 * on the marketing "Get a Fare in Seconds" home form. Stored as base64
 * data-URLs on payment_config.storefront (small — 200×200 PNGs typically
 * come in under 5 KB) so no separate media-upload pipeline is required.
 *
 * Public endpoint `/api/storefront/payment-qrs` serves whatever is saved
 * here; the marketing form polls it on mount.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Camera, Loader2, X, QrCode, Save, DollarSign,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

const MAX_QR_BYTES = 500 * 1024; // 500 KB — plenty for any reasonable QR image

async function fileToDataUrl(file) {
  if (file.size > MAX_QR_BYTES) {
    throw new Error(`Image too large (${(file.size / 1024).toFixed(0)} KB > 500 KB) — please export a smaller QR.`);
  }
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

export default function StorefrontQRCard() {
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/payment-config");
      setCfg((r.data?.storefront || {
        cashapp_qr_url: "", cashapp_handle: "", venmo_qr_url: "", venmo_handle: "",
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load storefront config");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    const next = { ...(cfg || {}), ...patch };
    setCfg(next);
    try {
      await adminApi.put("/admin/payment-config", { storefront: next });
      toast.success("Storefront saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      load(); // rollback from server
    }
  };

  if (!cfg) return null;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3" data-testid="storefront-qr-card">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center">
          <QrCode className="h-5 w-5 text-emerald-300"/>
        </div>
        <div>
          <div className="text-base font-bold text-zinc-100">Storefront Digital Wallet QRs</div>
          <div className="text-[11px] text-zinc-500">Displayed on the &quot;Get a Fare in Seconds&quot; home form so riders can pay you directly via Cash App or Venmo without needing a card.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <QRSlot
          label="Cash App"
          testidPrefix="storefront-cashapp"
          handleValue={cfg.cashapp_handle || ""}
          handlePlaceholder="$BullerTaxi"
          imgUrl={cfg.cashapp_qr_url || ""}
          onSaveHandle={(v) => save({ cashapp_handle: v })}
          onSaveImg={(url)  => save({ cashapp_qr_url: url })}
        />
        <QRSlot
          label="Venmo"
          testidPrefix="storefront-venmo"
          handleValue={cfg.venmo_handle || ""}
          handlePlaceholder="@BullerTaxi"
          imgUrl={cfg.venmo_qr_url || ""}
          onSaveHandle={(v) => save({ venmo_handle: v })}
          onSaveImg={(url)  => save({ venmo_qr_url: url })}
        />
      </div>
    </Card>
  );
}

function QRSlot({ label, testidPrefix, handleValue, handlePlaceholder, imgUrl,
                   onSaveHandle, onSaveImg }) {
  const fileRef = useRef(null);
  const [handle, setHandle] = useState(handleValue);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setHandle(handleValue); }, [handleValue]);

  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      await onSaveImg(dataUrl);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${label} QR from the home form?`)) return;
    await onSaveImg("");
  };

  return (
    <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid={`${testidPrefix}-slot`}>
      <div className="flex items-center gap-2 mb-2">
        <DollarSign className="h-4 w-4 text-amber-300"/>
        <div className="font-bold text-zinc-100 text-sm">{label}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Handle</Label>
          <div className="flex gap-1.5 mt-1">
            <Input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder={handlePlaceholder}
              className="bg-zinc-900 border-zinc-700 text-xs h-8"
              data-testid={`${testidPrefix}-handle`}
            />
            <Button
              onClick={() => onSaveHandle(handle.trim())}
              size="sm"
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 h-8 px-2 font-bold text-[10px]"
              data-testid={`${testidPrefix}-save-handle`}
            >
              <Save className="h-3 w-3"/>
            </Button>
          </div>
          <p className="text-[9px] text-zinc-500 mt-1 leading-tight">Shown under the QR image on the home form.</p>
          <div className="mt-3">
            <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testidPrefix}-input`}/>
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              variant="outline"
              size="sm"
              className="bg-zinc-950 border-zinc-700 w-full text-[11px]"
              data-testid={`${testidPrefix}-upload`}
            >
              {busy
                ? <><Loader2 className="h-3 w-3 animate-spin mr-1"/>Uploading…</>
                : <><Camera className="h-3 w-3 mr-1"/>{imgUrl ? `Replace ${label} QR` : `Upload ${label} QR`}</>}
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-center">
          {imgUrl ? (
            <div className="relative">
              <img
                src={imgUrl}
                alt={`${label} QR`}
                className="h-32 w-32 object-contain rounded bg-white p-1.5 border-2 border-emerald-400/40"
                data-testid={`${testidPrefix}-image`}
              />
              <button
                onClick={remove}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-600 hover:bg-rose-500 text-white flex items-center justify-center"
                data-testid={`${testidPrefix}-remove`}
              >
                <X className="h-3 w-3"/>
              </button>
            </div>
          ) : (
            <div className="h-32 w-32 rounded bg-zinc-900 border-2 border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 text-[10px] text-center px-2">
              No {label} QR uploaded yet
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
